import { zodResolver } from "@hookform/resolvers/zod";
import { Dispatch, FunctionComponent, SetStateAction } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { faBriefcase, faGlobe } from "@fortawesome/pro-regular-svg-icons";

import { Icon, Modal } from "@/components";

import { useCreateCompaniesMutation } from "@/pages/Private/redux/companies/companies.api";

import FormTextField from "../../Account/components/FormTextField";
import { CreateCompanies, CreateCompaniesSchema } from "../schema/companies";

interface createCompaniesModalProps {
	isOpen: boolean;
	setIsOpen: Dispatch<SetStateAction<boolean>>;
}

export const CreateCompaniesModal: FunctionComponent<createCompaniesModalProps> = ({
	isOpen,
	setIsOpen,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`companies.add.${key}`);
	const [createCompanies, { isLoading, error }] = useCreateCompaniesMutation();

	const {
		handleSubmit,
		register,
		formState: { errors },
	} = useForm<CreateCompanies>({
		defaultValues: {
			name: undefined,
			website: undefined,
		},
		resolver: zodResolver(CreateCompaniesSchema),
	});

	const onSubmit = async (values: CreateCompanies) => {
		try {
			await createCompanies(values).unwrap();
			setIsOpen(false);
		} catch (err) {
			console.error(err);
		}
	};

	return (
		<Modal
			aboveHeader={<Icon className="w-4 h-4" icon={faBriefcase} />}
			dialogContentClassName="!p-[2px]"
			formError={error}
			handleClose={() => setIsOpen(false)}
			handleSave={handleSubmit(onSubmit)}
			isLoading={isLoading}
			isOpened={isOpen}
			submitButtonText={t("basics.add")}
			subtitle={ts("subtitle")}
			title={ts("title")}
		>
			<FormTextField
				errorMessage={errors?.name?.message}
				formRegister={register("name")}
				label={t("companies.name")}
				startAdornment={
					<Icon className="w-4 h-4 mr-2 text-primary-gray-lighter" icon={faBriefcase} />
				}
			/>
			<FormTextField
				errorMessage={errors?.website?.message}
				formRegister={register("website")}
				label={t("companies.website")}
				required={false}
				startAdornment={<Icon className="w-4 h-4 mr-2 text-primary-gray-lighter" icon={faGlobe} />}
			/>
		</Modal>
	);
};
